import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { useAuth } from '../../context/AuthContext';
import {
  createActivityNameMap,
  getActivities,
} from '../../services/activityService';
import {
  createTrainingClassMap,
  getTrainingClasses,
} from '../../services/trainingClassService';
import { getTrainingSessions } from '../../services/trainingSessionService';
import DashboardChartCard from './DashboardChartCard';
import {
  buildActivityClassTotals,
  buildActivityDurationByPeriod,
  buildActivityOptions,
  buildActivityStats,
  durationOptions,
  filterSessionsByDuration,
  isMonthlyGroupingDisabled,
} from './dashboardOverviewUtils';

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
);

function DashboardActivities({
  period,
  duration,
  setPeriod,
  setDuration,
}) {
  const [selectedActivityId, setSelectedActivityId] = useState('');
  const [sessions, setSessions] = useState([]);
  const [activityNamesById, setActivityNamesById] = useState({});
  const [trainingClassesByName, setTrainingClassesByName] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { accessToken, logout } = useAuth();
  const theme = useTheme();
  const chartColors = useMemo(
    () => theme.customTokens?.chartPalette || [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      '#2a9d8f',
      '#e9c46a',
      '#f4a261',
      '#e76f51',
      '#64748b',
    ],
    [theme]
  );

  useEffect(() => {
    if (!accessToken) {
      setLoading(false);
      logout({ shouldCallApi: false });
      return;
    }

    const loadDashboardData = async () => {
      try {
        const [sessionsData, activitiesData, trainingClassesData] = await Promise.all([
          getTrainingSessions(accessToken),
          getActivities(accessToken),
          getTrainingClasses(accessToken),
        ]);

        const activities = Array.isArray(activitiesData) ? activitiesData : [];
        setSessions(Array.isArray(sessionsData) ? sessionsData : []);
        setActivityNamesById(createActivityNameMap(activities));
        setTrainingClassesByName(createTrainingClassMap(Array.isArray(trainingClassesData) ? trainingClassesData : []));
      } catch (err) {
        if (err?.status === 401) {
          await logout({ shouldCallApi: false });
          return;
        }

        setError(err.message || 'Failed to load activity dashboard data.');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [accessToken, logout]);

  const filteredSessions = useMemo(
    () => filterSessionsByDuration(sessions, duration),
    [duration, sessions]
  );
  const activityOptions = useMemo(
    () => buildActivityOptions(filteredSessions, activityNamesById),
    [activityNamesById, filteredSessions]
  );

  useEffect(() => {
    if (activityOptions.length === 0) {
      setSelectedActivityId('');
      return;
    }

    const availableActivityIds = new Set(activityOptions.map((activity) => activity.id));
    setSelectedActivityId((currentActivityId) => (
      availableActivityIds.has(currentActivityId)
        ? currentActivityId
        : activityOptions[0].id
    ));
  }, [activityOptions]);

  const selectedActivityName = useMemo(
    () => activityOptions.find((activity) => activity.id === selectedActivityId)?.name || 'Activity',
    [activityOptions, selectedActivityId]
  );
  const activityDurationByPeriod = useMemo(
    () => buildActivityDurationByPeriod(filteredSessions, period, selectedActivityId),
    [filteredSessions, period, selectedActivityId]
  );
  const activityClassTotals = useMemo(
    () => buildActivityClassTotals(filteredSessions, selectedActivityId, trainingClassesByName),
    [filteredSessions, selectedActivityId, trainingClassesByName]
  );
  const activityStats = useMemo(
    () => buildActivityStats(filteredSessions, selectedActivityId),
    [filteredSessions, selectedActivityId]
  );

  const baseChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: theme.customTokens?.chartTick || theme.palette.text.secondary,
        },
      },
      tooltip: {
        backgroundColor: theme.customTokens?.chartTooltipBg,
        titleColor: theme.customTokens?.chartTooltipText,
        bodyColor: theme.customTokens?.chartTooltipText,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        callbacks: {
          label: (context) => `${context.dataset.label}: ${Number(context.raw) || 0} min`,
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: theme.customTokens?.chartTick || theme.palette.text.secondary,
          maxRotation: 0,
          autoSkip: true,
        },
        grid: {
          color: theme.customTokens?.chartGrid || theme.palette.divider,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: theme.customTokens?.chartTick || theme.palette.text.secondary,
          callback: (value) => `${value}m`,
        },
        grid: {
          color: theme.customTokens?.chartGrid || theme.palette.divider,
        },
      },
    },
  }), [theme]);

  const lineOptions = useMemo(() => ({
    ...baseChartOptions,
    plugins: {
      ...baseChartOptions.plugins,
      legend: { display: false },
    },
  }), [baseChartOptions]);

  const stackedBarOptions = useMemo(() => ({
    ...baseChartOptions,
    indexAxis: 'y',
    plugins: {
      ...baseChartOptions.plugins,
      legend: {
        position: 'bottom',
        labels: {
          color: theme.customTokens?.chartTick || theme.palette.text.secondary,
          usePointStyle: true,
          boxWidth: 8,
        },
      },
    },
    scales: {
      x: {
        ...baseChartOptions.scales.x,
        beginAtZero: true,
        stacked: true,
        ticks: {
          ...baseChartOptions.scales.x.ticks,
          callback: (value) => `${value}m`,
        },
      },
      y: {
        ...baseChartOptions.scales.y,
        stacked: true,
        ticks: {
          ...baseChartOptions.scales.y.ticks,
          autoSkip: false,
          callback: (value) => {
            const label = activityClassTotals.labels[value] || '';
            return label.length > 24 ? `${label.slice(0, 21)}...` : label;
          },
        },
        grid: {
          display: false,
        },
      },
    },
  }), [activityClassTotals.labels, baseChartOptions, theme]);

  const activityLineData = useMemo(() => ({
    labels: activityDurationByPeriod.labels,
    datasets: [
      {
        label: `${selectedActivityName} minutes`,
        data: activityDurationByPeriod.values,
        borderColor: theme.customTokens?.lineColor || theme.palette.primary.main,
        backgroundColor: theme.customTokens?.lineFill || theme.palette.primary.main,
        tension: 0.28,
        pointRadius: 3,
        pointHoverRadius: 4,
        pointBackgroundColor: theme.customTokens?.lineColor || theme.palette.primary.main,
        pointBorderColor: theme.palette.background.paper,
        pointBorderWidth: 2,
      },
    ],
  }), [
    activityDurationByPeriod.labels,
    activityDurationByPeriod.values,
    selectedActivityName,
    theme,
  ]);
  const getStackedBarRadius = useCallback((context) => {
    const selectedActivityVisible = context.chart.isDatasetVisible(0);
    const otherActivityVisible = context.chart.isDatasetVisible(1);

    if (!selectedActivityVisible || !otherActivityVisible) {
      return 5;
    }

    return context.datasetIndex === 0
      ? { bottomLeft: 5, topLeft: 5 }
      : { bottomRight: 5, topRight: 5 };
  }, []);
  const otherActivityColor = theme.palette.mode === 'dark'
    ? 'rgba(148, 163, 184, 0.34)'
    : 'rgba(100, 116, 139, 0.24)';

  const activityClassStackedBarData = useMemo(() => ({
    labels: activityClassTotals.labels,
    datasets: [
      {
        label: selectedActivityName,
        data: activityClassTotals.selectedValues,
        backgroundColor: chartColors[0],
        borderRadius: getStackedBarRadius,
        borderSkipped: false,
        barPercentage: 0.72,
        categoryPercentage: 0.74,
      },
      {
        label: 'Other Activities',
        data: activityClassTotals.otherValues,
        backgroundColor: otherActivityColor,
        hidden: true,
        borderRadius: getStackedBarRadius,
        borderSkipped: false,
        barPercentage: 0.72,
        categoryPercentage: 0.74,
      },
    ],
  }), [
    activityClassTotals.labels,
    activityClassTotals.otherValues,
    activityClassTotals.selectedValues,
    chartColors,
    getStackedBarRadius,
    otherActivityColor,
    selectedActivityName,
  ]);

  const activityStatsCards = useMemo(() => ([
    {
      label: `${selectedActivityName} Total Time`,
      value: `${activityStats.totalMinutes} min`,
    },
    {
      label: `Sessions With ${selectedActivityName}`,
      value: activityStats.sessionCount,
    },
    {
      label: 'Average Per Time Logged',
      value: `${activityStats.averageMinutes} min`,
    },
  ]), [activityStats, selectedActivityName]);
  const activityClassChartHeight = useMemo(
    () => Math.max(300, (activityClassTotals.labels.length * 46) + 96),
    [activityClassTotals.labels.length]
  );

  if (loading) {
    return (
      <Box className="dashboard-loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="dashboard-overview dashboard-activities">
      {error && (
        <Alert severity="error" className="dashboard-error">
          {error}
        </Alert>
      )}

      <Paper className="dashboard-activity-drilldown" elevation={0}>
        <Box className="dashboard-activity-drilldown-header">
          <Box className="dashboard-activity-title-block">
          </Box>
          <Box className="dashboard-activity-controls">
            <FormControl className="dashboard-filter-control dashboard-activity-filter" size="small">
              <InputLabel id="dashboard-activity-label">Activity</InputLabel>
              <Select
                labelId="dashboard-activity-label"
                value={selectedActivityId}
                label="Activity"
                onChange={(event) => setSelectedActivityId(event.target.value)}
                disabled={activityOptions.length === 0}
              >
                {activityOptions.map((activity) => (
                  <MenuItem key={activity.id} value={activity.id}>
                    {activity.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl className="dashboard-filter-control" size="small">
              <InputLabel id="dashboard-activity-period-label">Grouping</InputLabel>
              <Select
                labelId="dashboard-activity-period-label"
                value={period}
                label="Grouping"
                onChange={(event) => setPeriod(event.target.value)}
              >
                <MenuItem value="week">Weekly</MenuItem>
                <MenuItem value="month" disabled={isMonthlyGroupingDisabled(duration)}>
                  Monthly
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl className="dashboard-filter-control" size="small">
              <InputLabel id="dashboard-activity-duration-label">Duration</InputLabel>
              <Select
                labelId="dashboard-activity-duration-label"
                value={duration}
                label="Duration"
                onChange={(event) => setDuration(event.target.value)}
              >
                {durationOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Paper>

      <Box className="dashboard-activity-stats">
        {activityStatsCards.map((stat) => (
          <Paper key={stat.label} className="dashboard-activity-stat-card" elevation={0}>
            <Typography variant="subtitle2">{stat.label}</Typography>
            <Typography variant="h5" component="p">{stat.value}</Typography>
          </Paper>
        ))}
      </Box>

      <Box className="dashboard-activity-chart-grid">
        <DashboardChartCard
          title={`${selectedActivityName} Trend`}
          isEmpty={activityDurationByPeriod.values.every((value) => value === 0)}
          emptyMessage="No duration data found for this activity and duration."
          cardClassName="dashboard-activity-trend-card"
        >
          <Line data={activityLineData} options={lineOptions} />
        </DashboardChartCard>

        <DashboardChartCard
          title={`${selectedActivityName} by Class`}
          isEmpty={activityClassTotals.labels.length === 0}
          emptyMessage="No class data found for this activity and duration."
          cardClassName="dashboard-activity-class-card"
          chartFrameClassName="dashboard-activity-class-frame"
          chartFrameStyle={{ height: activityClassChartHeight }}
        >
          <Bar data={activityClassStackedBarData} options={stackedBarOptions} />
        </DashboardChartCard>
      </Box>
    </Box>
  );
}

export default DashboardActivities;
